import { useEffect, useState } from "react";

interface ProductListProps {
  limit: number;
  currentPage: number;
  type: string;
  onError?: () => void;
}

const STORE_URL = "https://smartstore.naver.com/cct7007";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

interface Product {
  originProductNo: number | string;
  name: string;
  price?: number;
  imageUrl?: string;
}

interface ProductSearchResponse {
  contents?: Array<Record<string, any>>;
}

const getProduct = (item: Record<string, any>): Product => {
  const channelProduct = item.channelProducts?.[0] || item;
  const imageUrl =
    channelProduct.representativeImage?.url ||
    channelProduct.representativeImageUrl;
  return {
    originProductNo:
      item.originProductNo ||
      channelProduct.originProductNo ||
      channelProduct.channelProductNo,
    name:
      item.name ||
      item.productName ||
      channelProduct.name ||
      channelProduct.productName ||
      "상품명 미제공",
    price: channelProduct.salePrice || channelProduct.price,
    imageUrl,
  };
};

const ProductList = ({ limit, currentPage, onError }: ProductListProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const loadProducts = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/products?page=${currentPage}&size=${limit}`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error("상품 목록 조회 실패");
        const data: ProductSearchResponse = await response.json();
        setProducts(
          (data.contents || [])
            .map(getProduct)
            .filter((product) => product.originProductNo)
            .slice(0, limit),
        );
      } catch (requestError) {
        if ((requestError as Error).name !== "AbortError") {
          setError(true);
          onError?.();
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
    return () => controller.abort();
  }, [currentPage, limit, onError]);

  if (isLoading)
    return (
      <section className="product-store-cta" aria-live="polite">
        상품을 불러오는 중입니다.
      </section>
    );

  if (error || products.length === 0) {
    if (onError) return null;

    return (
      <section className="product-store-cta" aria-label="제때팜 공식 쇼핑몰">
        <p className="product-store-cta__eyebrow">JETTE FARM STORE</p>
        <h2>제때팜의 건강한 먹거리</h2>
        <p>
          판매 중인 상품과 재고, 가격은 공식 네이버 스마트스토어에서 가장
          정확하게 확인할 수 있습니다.
        </p>
        <a href={STORE_URL} target="_blank" rel="noreferrer">
          공식 스마트스토어에서 상품 보기
        </a>
      </section>
    );
  }

  return (
    <section className="commerce-product-list" aria-label="제때팜 상품 목록">
      {products.map((product) => (
        <a
          className="commerce-product"
          href={`${STORE_URL}/products/${product.originProductNo}`}
          target="_blank"
          rel="noreferrer"
          key={product.originProductNo}
        >
          {product.imageUrl ? (
            <img src={product.imageUrl} alt="" />
          ) : (
            <div className="commerce-product__image-placeholder" />
          )}
          <h2>{product.name}</h2>
          {product.price !== undefined && (
            <p>{product.price.toLocaleString("ko-KR")}원</p>
          )}
        </a>
      ))}
    </section>
  );
};

export default ProductList;
